import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { PaymentService } from '../payment.service';
import { PaymentMethodKind, PaymentMethodType } from '../payment.type';

export interface PaymentMethodDialogData {
  /** Absent to add a new method. */
  method?: PaymentMethodType;
  /** From getRateSources(): 'Manual' plus the registered providers. */
  rateSources: string[];
}

/**
 * Adds or edits a payout method. A wallet method also says where its coin's
 * Toman rate comes from: a provider (with its currency code, checked against the
 * provider on save) or a manual rate. A manual rate can be kept on a provider
 * method too, ready to switch to if the provider goes down.
 */
@Component({
  selector: 'app-payment-method-dialog',
  imports: [
    CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatIconModule,
    MatInputModule, MatProgressSpinnerModule, MatSelectModule,
  ],
  templateUrl: './payment-method-dialog.html',
  styleUrl: './payment-method-dialog.scss',
})
export class PaymentMethodDialog {
  private dialogRef = inject(MatDialogRef<PaymentMethodDialog, PaymentMethodType | null>);
  private paymentService = inject(PaymentService);
  readonly data = inject<PaymentMethodDialogData>(MAT_DIALOG_DATA);

  readonly editing = !!this.data.method;
  kind: PaymentMethodKind = this.data.method?.kind ?? 'Wallet';
  name = this.data.method?.name ?? '';
  addressPattern = this.data.method?.address_pattern ?? '';
  rateSource = this.initialSource();
  rateSymbol = this.data.method?.rate_symbol ?? '';
  manualRate = this.data.method?.manual_rate ?? '';
  amountDecimals = this.data.method?.amount_decimals ?? 2;
  /** '' = the admin pays by hand; 'BSC' = sent from the hot wallet (USDT BEP-20). */
  onchainNetwork = this.data.method?.onchain_network ?? '';

  saving = false;
  error = '';

  get isManual(): boolean { return this.rateSource === 'Manual'; }

  private initialSource(): string {
    const current = this.data.method?.rate_source;
    if (current && current !== 'None') return current;
    return this.data.rateSources.find(s => s !== 'Manual') ?? 'Manual';
  }

  save(): void {
    if (!this.name.trim()) { this.error = 'Name is required'; return; }
    const wallet = this.kind === 'Wallet';
    const body = {
      name: this.name.trim(),
      address_pattern: wallet ? this.addressPattern.trim() : undefined,
      rate_source: wallet ? this.rateSource : undefined,
      rate_symbol: wallet ? this.rateSymbol.trim() : undefined,
      manual_rate: wallet ? String(this.manualRate ?? '').trim() : undefined,
      amount_decimals: wallet ? Number(this.amountDecimals) : undefined,
      onchain_network: wallet ? this.onchainNetwork : undefined,
    };
    this.saving = true;
    this.error = '';
    const call = this.data.method
      ? this.paymentService.updatePaymentMethodType(this.data.method.id, body)
      : this.paymentService.createPaymentMethodType({
        ...body,
        kind: this.kind,
        address_pattern: body.address_pattern || undefined,
        manual_rate: body.manual_rate || undefined,
        onchain_network: body.onchain_network || undefined,
      });
    call.subscribe({
      next: m => this.dialogRef.close(m),
      error: err => { this.saving = false; this.error = err?.error?.error ?? 'Failed to save'; },
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
